import { ApiConfigProvider } from "shared-ui/src/contexts";
import { AppendIfPrefixExists } from "../modules/sitePaths";
import { AwsGovernanceReports } from "./Modules/Cloud/AWS/AwsSecurityCompliance/AwsGovernanceReports";
import { AzureGovernanceReports } from "./Modules/Cloud/Azure/AzureGovernanceReports";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { detect } from "detect-browser";
import { GetBearerToken } from "../auth/BearerToken";
// GetBearerToken can return null | undefined when auth fails; wrap it so
// ApiConfigProvider always receives () => string | Promise<string>.
const getBearerTokenSafe = (): Promise<string> =>
  GetBearerToken().then((t) => t ?? "");
import { LogoutComponent } from "./Authorization/LogoutComponent";
import { Navigate, Route, Routes } from "react-router-dom";
import { security } from "../auth/security";
import { telemetryBeginUser, telemetryEndUser } from "../functions/telemetry";
import { ThemeProvider as EmotionThemeProvider } from "@emotion/react";
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useMemo, lazy, Suspense } from "react";
import { useClaims } from "../auth/ClaimsProvider";
import { useInternalUserRedirect } from "../auth/useInternalUserRedirect";
import { UserFlow } from "@components/UserFlow";
import { useThemeMode } from "../theme/ThemeModeProviderContext";
import ArchbeeShiInternal from "../components/ArchbeeShiInternal";
import AuthError from "./AuthError";
import CustomerExperienceTwoColumnDashboard from "../v2/src/dashboards/CustomerExperienceTwoColumnDashboard";
import DemoAccounts from "./Modules/SHI/DemoAccounts";
import DemoShipping from "./Modules/AssetManagement/Warehouse/DemoShipping";
import ErrorBoundary from "../IndexErrorBoundary";
import ErrorPage from "./ErrorPage";
import FeatureComponent from "../components/FeatureComponent";
import DevOpsPostureReviewRouteGuard from "@features/assessments-feature/components/dev-ops-posture-review/DevOpsPostureReviewRouteGuard";
import HomeDashboard from "./HomeDashboard";
import UnifiedHomeDashboard from "./Modules/UnifiedHomeDashboard/UnifiedHomeDashboard";
import InvalidUrlPage from "./InvalidUrlPage";
import Loading from "../components/Loading";
import LoginComponent from "./Authorization/LoginComponent";
import Microsoft365ReportingRegistrationCallback from "./Callbacks/Microsoft365ReportingRegistrationCallback";
import { ImpersonationBridge } from "../components/ImpersonationBridge";
import PasswordlessLogin from "./Authorization/PasswordlessLogin";
import ProjectDetailsV2 from "@pages/services/projects/ProjectDetailsV2";
import ReactRouterClassWrapper from "../components/ReactRouterClassWrapper";
import RegisterPage from "./RegisterPage";
import RoadmapsRecommendationsMiddleware from "../middleware/RoadmapsRecommendationsMiddleware.js";
import SamlLogoutComponent from "./Authorization/SamlLogoutComponent";
import SetWindowLocation from "../components/SetWindowLocation";
import SuccessRequestCallback from "./SuccessRequestCallback";
import StorefrontRouteGuard from "../v2/src/components/StorefrontRouteGuard";
import { PRODUCT_LISTING_ROUTE_PATH } from "../v2/src/pages/ecommerce/product-listing/productListingRoutes";
import WelcomeCallback from "./WelcomeCallback";
import { useFeatureFlag } from "shared-ui/src/utils/FeatureFlagHelper.js";
import { ShiOnePermission } from "../auth/authorizationTypes";
import { TelemetryUser } from "../functions/telemetryUser";
import { parseCrmReportingBuPath } from "shared-ui/src/utils/parseCrmReportingBuPath";
import { markShiOneIdentityAbsent } from "shared-ui/src/instrumentation/gtm";
import { useGetLinkedItProjectsByRoadmap } from "../v2/src/features/projects-feature/hooks/useGetLinkedItProjects";
import { useGetStorefronts } from "@features/storefronts-feature/hooks/useGetStorefronts";
import { standardsDialogOverlayPaths } from "@features/ecommerce/constants/standardsRouteSegments";
import { bundleOverlayPaths } from "@features/ecommerce/constants/bundleRouteSegments";
import { STANDARDS_ONLY_CONFIRM_SEGMENT } from "shared-ui/src/components/ecommerce/catalog/storefront-tab/routeSegments";
import QuotePageRoute from "./QuotePageRoute";
import {
  awsAccountsOverlayPaths,
  awsCostAllocationTagsOverlayPaths,
  awsRecommendationsOverlayPaths,
  azureRecommendationsOverlayPaths,
  azureSecurityComplianceOverlayPaths,
  azureSubscriptionsOverlayPaths,
  cloudSubscriptionsOverlayPaths,
  costCentersOverlayPaths,
  cspOverlayPaths,
  marketplaceAccountsOverlayPaths,
} from "@features/cloud-feature/constants/cloudRouteSegments";

// Routed overlays a splat host accepts; any other remainder renders the not-found page.
const QUOTE_DETAIL_OVERLAY_PATHS = [
  "/:prefix?/quotes/:quoteId/share",
  "/:prefix?/quotes/:quoteId/refresh",
  "/:prefix?/quotes/:quoteId/storefront-mismatch",
  "/:prefix?/quotes/:quoteId/item/:productRouteId",
  "/:prefix?/quotes/:quoteId/item/:productRouteId/:tab",
];
const STOREFRONT_OVERLAY_PATHS = [
  ...bundleOverlayPaths("/:prefix?/settings/storefronts/:storefrontId/bundles"),
  "/:prefix?/settings/storefronts/:storefrontId/standards/item/:productRouteId",
  "/:prefix?/settings/storefronts/:storefrontId/standards/item/:productRouteId/:tab",
  ...standardsDialogOverlayPaths(
    "/:prefix?/settings/storefronts/:storefrontId/standards",
  ),
  `/:prefix?/settings/storefronts/:storefrontId/catalog/${STANDARDS_ONLY_CONFIRM_SEGMENT}`,
];
const STANDARDS_MANAGEMENT_OVERLAY_PATHS = [
  "/:prefix?/standards-management/item/:productRouteId",
  "/:prefix?/standards-management/item/:productRouteId/:tab",
  ...standardsDialogOverlayPaths("/:prefix?/standards-management"),
];
const STANDARDS_SHOP_OVERLAY_PATHS = [
  "/:prefix?/standards-shop/item/:productRouteId",
  "/:prefix?/standards-shop/item/:productRouteId/:tab",
];
const SERVICE_CATALOG_OVERLAY_PATHS = [
  "/:prefix?/service-catalog/service/:serviceId",
];
const CLOUD_SUBSCRIPTIONS_OVERLAY_PATHS = cloudSubscriptionsOverlayPaths(
  "/:prefix?/cloud/subscriptions",
);
const CSP_OVERLAY_PATHS = cspOverlayPaths(
  "/:prefix?/cloud/subscription-management",
);
const COST_CENTERS_OVERLAY_PATHS = costCentersOverlayPaths(
  "/:prefix?/cloud/cost-centers",
);
const AZURE_SECURITY_COMPLIANCE_OVERLAY_PATHS =
  azureSecurityComplianceOverlayPaths("/:prefix?/cloud/azure/securitycompliance");
const AZURE_RECOMMENDATIONS_OVERLAY_PATHS = azureRecommendationsOverlayPaths(
  "/:prefix?/cloud/azure/recommendations",
);
const AZURE_SUBSCRIPTIONS_OVERLAY_PATHS = azureSubscriptionsOverlayPaths(
  "/:prefix?/cloud/azure/subscriptions",
);
const AWS_ACCOUNTS_OVERLAY_PATHS = awsAccountsOverlayPaths(
  "/:prefix?/cloud/aws/accounts",
);
const AWS_COST_ALLOCATION_TAGS_OVERLAY_PATHS =
  awsCostAllocationTagsOverlayPaths("/:prefix?/cloud/aws/cat");
const AWS_RECOMMENDATIONS_OVERLAY_PATHS = awsRecommendationsOverlayPaths(
  "/:prefix?/cloud/aws/recommendations",
);
const MARKETPLACE_ACCOUNTS_OVERLAY_PATHS = marketplaceAccountsOverlayPaths(
  "/:prefix?/shi-internal/marketplaceAccounts",
);

const AddressFieldTestPage = lazy(
  () => import("./Widgets/AddressFieldTestPage"),
);
const CostCentersPage = lazy(
  () => import("../v2/src/pages/cloud/CostCentersPage"),
);
const ServicesBillingPage = lazy(
  () => import("../v2/src/pages/services/invoices/ServicesBillingPage"),
);
const StandardsManagementPage = lazy(
  () => import("../v2/src/pages/ecommerce/StandardsManagementPage"),
);
const StandardsShopRoutePage = lazy(
  () => import("../v2/src/pages/ecommerce/StandardsShopRoutePage"),
);
const ProductListingRoutePage = lazy(
  () => import("../v2/src/pages/ecommerce/ProductListingRoutePage"),
);
const HomeFeatureFlagged = lazy(() => import("./Home"));
const BusinessReviewPage = lazy(
  () => import("@pages/account-information/AccountInformationPage"),
);
const ServiceRequestDetails = lazy(
  () => import("../v2/src/pages/service-requests/ServiceRequestDetails"),
);
const Monitoring = lazy(() => import("@pages/cloud/MonitoringPage"));
const AzureReport = lazy(() => import("./Modules/Cloud/Azure/AzureReport"));
const AzureAllUpReport = lazy(
  () => import("./Modules/Cloud/Azure/AzureAllUpReport"),
);
const DatadogReportPage = lazy(
  () => import("./Modules/Cloud/Datadog/DatadogReportPage"),
);
const IdcsReportPage = lazy(
  () => import("@pages/report-center/IdcsReportPage"),
);
const AwsMonitoring = lazy(() => import("./Modules/Cloud/AWS/AwsMonitoring"));
const AzureMonitoring = lazy(
  () => import("./Modules/Cloud/Azure/AzureMonitoring"),
);
const DeviceList = lazy(() => import("./Modules/MySupport/DeviceList"));
const ITKnowledgeBase = lazy(
  () => import("./Modules/SupportCenter/ITKnowledgeBase"),
);
const Settings = lazy(() => import("./Modules/Settings/Settings"));
const DownloadAttachment = lazy(
  () => import("../components/DownloadAttachment"),
);
const AwsAccounts = lazy(
  () => import("./Modules/Cloud/AWS/AwsAccounts/AwsAccounts"),
);
const AwsSimpleRecommendations = lazy(
  () =>
    import(
      "./Modules/Cloud/AWS/AwsSimpleRecommendations/AwsSimpleRecommendations"
    ),
);
const AwsCostAllocationTags = lazy(
  () => import("./Modules/Cloud/AWS/AwsCostAllocationTags"),
);
const AwsInvoiceList = lazy(() => import("./Modules/Cloud/AWS/AwsInvoiceList"));
const AzureAccounts = lazy(() => import("./Modules/Cloud/Azure/AzureAccounts"));
const AzureInvoiceList = lazy(
  () => import("./Modules/Cloud/Azure/AzureInvoiceList"),
);
const AzureBackup = lazy(() => import("./Modules/Cloud/Azure/AzureBackup"));
const AzureSecurityCompliance = lazy(
  () =>
    import(
      "./Modules/Cloud/Azure/AzureSecurityCompliance/AzureSecurityCompliance"
    ),
);
const Office365Accounts = lazy(
  () => import("./Modules/Cloud/Office365/Office365Accounts"),
);
const Office365InvoiceList = lazy(
  () => import("./Modules/Cloud/Office365/Office365InvoiceList"),
);
const TeamsManagement = lazy(
  () => import("@pages/teams-management/TeamsManagement"),
);
const Projects = lazy(() => import("../v2/src/pages/services/Projects"));
const MyProjectList = lazy(() => import("./Modules/SHI/MyProjectList"));
const ContractMappings = lazy(() => import("./Modules/SHI/ContractMappings"));
const ContractDetailPage = lazy(
  () => import("./Modules/Services/ContractDetailsPage"),
);
const IntegrationCenterOrder = lazy(
  () =>
    import(
      "@features/assets-feature/components/warehouse-orders/WarehouseIntegrationOrder"
    ),
);
const WarehouseOrder = lazy(
  () =>
    import(
      "@features/assets-feature/components/warehouse-orders/WarehouseTransferOrder"
    ),
);
const WarehouseOrderDetail = lazy(
  () => import("../v2/src/pages/assets/WarehouseOrderDetailPage"),
);
const OrganizationProfile = lazy(
  () => import("./Modules/Profiles/OrganizationProfile"),
);
const CloudReports = lazy(() => import("./Modules/SHI/CloudReports"));
const Integrations = lazy(() => import("./Modules/Settings/Integrations"));
const AzureSimpleRecommendations = lazy(
  () =>
    import(
      "./Modules/Cloud/Azure/AzureSimpleRecommendations/AzureSimpleRecommendations"
    ),
);
const MarketplaceAccounts = lazy(
  () => import("./Modules/SHI/MarketplaceAccounts"),
);
const Blueprints = lazy(() => import("./Modules/Cloud/Blueprints/Blueprints"));
const GcpInvoiceList = lazy(() => import("./Modules/Cloud/GCP/GcpInvoiceList"));
const GcpProjects = lazy(() => import("./Modules/Cloud/GCP/GcpProjects"));
const Contracts = lazy(() => import("../v2/src/pages/services/ContractsPage"));
const ServiceRequestMappings = lazy(
  () => import("./Modules/SHI/ServiceRequestMappings"),
);
const SubmitRequest = lazy(() => import("./Modules/MySupport/SubmitRequest"));
const UserManagement = lazy(
  () => import("../v2/src/pages/user-management/UserManagementPage"),
);
const UserProfile = lazy(() => import("./Modules/Profiles/UserProfile"));
const SetupWizard = lazy(() => import("../SetUpWizard/SetupWizard"));
const RegisterViaInviteLinkPage = lazy(
  () => import("./RegisterViaInviteLinkPage"),
);
const AwsReport = lazy(
  () => import("../containers/Modules/Cloud/AWS/AwsReport"),
);
const GcpReport = lazy(
  () => import("../containers/Modules/Cloud/GCP/GcpReport"),
);

const CloudConsumptionPage = lazy(() => import("@pages/cloud/ConsumptionPage"));

const ContractsPage = lazy(
  () => import("../v2/src/pages/services/ContractsPage"),
);

const CiscoIba = lazy(() => import("./Modules/SHI/Cisco/Components/CiscoIba"));

const Labs = lazy(() => import("@pages/labs/Labs"));

const ServiceCatalog = lazy(
  () => import("@pages/service-catalog/ServiceCatalog"),
);

const SubmitRequestHandler = lazy(
  () => import("./Modules/MySupport/SubmitRequestHandler"),
);

const CloudOnboardingReport = lazy(
  () => import("./Modules/SHI/CloudOnboardingReport"),
);

const AssetDashboard = lazy(() => import("@pages/assets/AssetDashboardPage"));

const AssetInventory = lazy(() => import("@pages/assets/AssetInventoryPage"));

const DiscoveredAssets = lazy(
  () => import("@pages/assets/DiscoveredAssetsPage"),
);

const ManagedAssets = lazy(() => import("@pages/assets/ManagedAssetsPage"));

const IntegrationCenter = lazy(
  () => import("@pages/assets/IntegrationCenterPage"),
);

const AssetLifecycleManagementPage = lazy(
  () => import("@pages/assets/AssetLifecycleManagementPage"),
);

const AssetLocations = lazy(() => import("@pages/assets/AssetLocationsPage"));

const AssetContracts = lazy(() => import("@pages/assets/AssetContractsPage"));

const PurchasedAssets = lazy(() => import("@pages/assets/PurchasedAssetsPage"));

const ManufacturerInsightsPage = lazy(
  () => import("@pages/manufacturer-insights/ManufacturerInsightsPage"),
);

const InvoiceLandingPage = lazy(
  () =>
    import("../v2/src/features/billing-feature/components/InvoiceLandingPage"),
);

const AssetProductLifecycle = lazy(
  () => import("@pages/assets/AssetLifecyclePage"),
);

const AssetProductCatalog = lazy(
  () => import("@pages/assets/AssetProductCatalogPage"),
);

const CustomPbi = lazy(() => import("@pages/self-service-reporting/CustomPbi"));

const ReportCenter = lazy(
  () => import("@pages/self-service-reporting/ReportCenter"),
);

const NewReport = lazy(() => import("@pages/self-service-reporting/NewReport"));

const Report = lazy(() => import("@pages/self-service-reporting/Report"));

const EditReport = lazy(
  () => import("@pages/self-service-reporting/EditReportPage"),
);

const CloudConsumptionReport = lazy(
  () => import("@pages/report-center/CloudConsumptionReportPage"),
);

const CloudGovernanceReport = lazy(
  () => import("@pages/report-center/CloudGovernanceReportPage"),
);

const MicrosoftAdoptionDashboard = lazy(
  () => import("@pages/report-center/MicrosoftAdoptionDashboard"),
);

const CiscoIbaDashboard = lazy(
  () => import("@pages/report-center/CiscoIbaReportPage"),
);

const AgedInventoryPage = lazy(
  () => import("@pages/report-center/AgedInventoryPage"),
);

const OrderReportPage = lazy(
  () => import("@pages/report-center/OrderReportPage"),
);

const ReportCenterServiceReport = lazy(
  () => import("@pages/report-center/ServiceReportPage"),
);

const MessageCenter = lazy(() => import("@pages/message-center/MessageCenter"));

const RequestLandingPage = lazy(
  () => import("@pages/support-center/ServiceRequestLandingPage"),
);

const ServiceRequestsPage = lazy(
  () => import("../v2/src/pages/service-requests/ServiceRequestsPage"),
);

const ViewOrdersPage = lazy(
  () => import("../v2/src/pages/orders/ViewOrdersPage"),
);

const CartPageRoute = lazy(
  () => import("../v2/src/pages/ecommerce/CartPageRoute"),
);
const CheckoutSuccessPage = lazy(
  () => import("../v2/src/pages/ecommerce/CheckoutSuccessPage"),
);
const CheckoutCorePage = lazy(
  () =>
    import("../v2/src/features/ecommerce/checkout-core/page/CheckoutCorePage"),
);

const OrderDetailsPage = lazy(
  () => import("../v2/src/pages/orders/OrderDetailsPage"),
);

const ViewQuotesPage = lazy(
  () => import("../v2/src/pages/quotes/ViewQuotesPage"),
);

const CreateQuotePage = lazy(
  () => import("../v2/src/pages/quotes/CreateQuotePage"),
);

const QuoteDetailPage = lazy(
  () => import("../v2/src/pages/quotes/QuoteDetailPage"),
);

const InternalServiceRequests = lazy(
  () => import("../internal/InternalServiceRequests"),
);

const InternalNewRequest = lazy(() => import("../internal/InternalNewRequest"));

const InternalServiceRequestDetails = lazy(
  () => import("../internal/InternalServiceRequestDetails"),
);

const InternalNewRequestForm = lazy(
  () => import("../internal/InternalNewRequestForm"),
);

const InternalServiceRequestMapping = lazy(
  () => import("../internal/InternalServiceRequestMapping"),
);

const AssessmentsPage = lazy(
  () => import("@pages/assessments/AssessmentsPage"),
);

const AssessmentViewPage = lazy(
  () => import("@pages/assessments/AssessmentViewPage"),
);

const AssessmentDashboardPage = lazy(
  () => import("@pages/assessments/AssessmentDashboardPage"),
);

const CloudMatrixPage = lazy(
  () =>
    import(
      "@features/assessments-feature/components/cloud-matrix/CloudMatrixPage"
    ),
);

const CloudSubscriptions = lazy(() => import("@pages/cloud/Subscriptions"));

const SubscriptionManagement = lazy(
  () => import("@pages/cloud/SubscriptionManagement"),
);

const MappingPage = lazy(() => import("@pages/mapping-engine/MappingPage"));

const Roadmaps = lazy(() => import("@pages/roadmaps/Roadmaps"));

const CatalystDashboard = lazy(
  () => import("@pages/catalyst/CatalystDashboard"),
);

const RoadmapDetailPage = lazy(
  () =>
    import("shared-ui/src/features/roadmaps-feature/pages/RoadmapDetailPage"),
);

const Recommendations = lazy(
  () => import("@pages/recommendations/RecommendationsPage"),
);

const ItProjectsPage = lazy(() => import("@pages/projects/ItProjectsPage"));

const ItProjectDetailPage = lazy(
  () => import("@pages/projects/ItProjectDetailPage"),
);

const CloudDashboard = lazy(() => import("@pages/cloud/CloudDashboardPage"));

const OptIntoUnity = lazy(() => import("@pages/unity/OptIntoUnity"));

const Storefronts = lazy(() => import("@pages/storefronts/StorefrontsPage"));

const StorefrontManagement = lazy(
  () => import("@pages/storefronts/StorefrontManagementPage"),
);

const MyAddressesPage = lazy(
  () => import("@pages/my-addresses/MyAddressesPage"),
);

const MicrosoftInsightsReportPage = lazy(
  () => import("@pages/report-center/MicrosoftInsightsPage"),
);

const MicrosoftInsightsAssessmentPage = lazy(
  () =>
    import(
      "@features/assessments-feature/components/microsoft-insights/MicrosoftInsightsPage"
    ),
);

const ViewInvoicesPage = lazy(
  () => import("../v2/src/pages/invoices/ViewInvoicesPage"),
);

// Demo-data scenario switcher; the constant condition lets other builds drop the chunk entirely.
const SynthScenarioPanel =
  import.meta.env.MODE === "synth"
    ? lazy(() => import("../../synth/ui/ScenarioPanel"))
    : null;

function App() {
  const { isLoading, error, getAccessTokenSilently, isAuthenticated } =
    useAuth0();
  const {
    getClaimValue,
    getClaimValueAsNumber,
    getClaimValueAsBoolean,
    hasPermission,
    isCustomerAdmin,
  } = useClaims();
  const canManageStandards = hasPermission(
    ShiOnePermission.featuredProductsAdd,
  );

  const { enabled: unityDashboard } = useFeatureFlag(
    "synergists.unity-dashboard",
    false,
  );
  const { enabled: createQuotesFlag } = useFeatureFlag(
    "spu.create-quotes",
    false,
  );
  const { enabled: invoicePortalEnabled } = useFeatureFlag(
    "synergists.enable-invoice-portal",
    false,
  );

  const { currentTheme } = useThemeMode();

  // The storefront the user is currently operating in, for usage telemetry -
  // resolved from whichever org unit is currently selected, not per-page.
  //
  // Memoized on `orgUnitIdCurrent` (a primitive) rather than depended on as the raw
  // `storefronts` array below: react-query hands back a new array reference on every
  // background refetch even when the data is unchanged, which would otherwise re-run
  // the identity effect - and its cleanup - on every refetch.
  const {
    data: storefronts,
    isSuccess: storefrontsLoaded,
    isError: storefrontsFailed,
  } = useGetStorefronts(isAuthenticated && !isLoading);
  const orgUnitIdCurrentRaw = getClaimValue("orgUnitIdCurrent");
  const orgUnitIdCurrent =
    orgUnitIdCurrentRaw != null ? Number(orgUnitIdCurrentRaw) : null;
  const storefrontId = useMemo(
    () => storefronts?.find((s) => s.orgUnitId === orgUnitIdCurrent)?.id,
    [storefronts, orgUnitIdCurrent],
  );

  // Redirect internal users to CS unless they have bypass cookie or are impersonating
  useInternalUserRedirect();

  const bypassAuth0Paths = ["/connectCallback", "/impersonation-bridge"];
  const shouldBypassAuth0 = bypassAuth0Paths.some((path) =>
    location.pathname.startsWith(path),
  );

  security.setAccessTokenSilently(getAccessTokenSilently);

  const browserType = detect();

  useEffect(() => {
    if (error) {
      console.error("Error fetching telemetry data:", error);
      telemetryEndUser();
    }

    if (!isLoading) {
      if (!isAuthenticated) {
        markShiOneIdentityAbsent();
      }
      const userEmail = getClaimValue("userEmail");
      const userId = getClaimValue("userId");
      const accountId = getClaimValueAsNumber("accountId");
      const accountName = getClaimValue("accountName");
      const isVirtualAdmin = getClaimValueAsBoolean("isVirtualAdmin");
      const isImpersonating = getClaimValueAsBoolean("isImpersonating");
      const userEntraId = getClaimValue("userEntraId");
      const crmReportingBuPath = getClaimValue("crmReportingBuPath");
      const parsedBuPath = parseCrmReportingBuPath(crmReportingBuPath);
      const employeeId = getClaimValue("shiComEmployeeId");
      const isGuestUser = getClaimValueAsBoolean("isGuestUser");
      const tenantId = getClaimValue("tenantId");
      const accountGroupId = getClaimValue("accountGroupId");
      const salesOrgStatus = getClaimValue("salesOrgStatus");

      if (userEmail) {
        const telemetryUser: TelemetryUser = {
          userEmail,
          userId: userId ?? null,
          accountId: accountId ?? null,
          accountName: accountName ?? null,
          role: isCustomerAdmin() ? "admin" : "user",
          accountDivision: parsedBuPath.division,
          accountDistrict: parsedBuPath.district,
          impersonatedBy: isImpersonating ? (userEntraId ?? null) : null,
          isVirtualAdmin,
          employeeId: employeeId ?? null,
          isGuestUser,
          tenantId: tenantId ?? null,
          accountGroupId: accountGroupId ?? null,
          storefrontId: storefrontId != null ? String(storefrontId) : null,
          isIdentityComplete: storefrontsLoaded || storefrontsFailed,
          salesOrg: salesOrgStatus
            ? {
                status: salesOrgStatus,
                aeId: getClaimValue("primaryAeEntraId") ?? undefined,
                reportingBuId:
                  getClaimValue("primaryAeReportingBuId") ?? undefined,
                reportingBuPath:
                  getClaimValue("primaryAeReportingBuPath") ?? undefined,
                salesKey: getClaimValue("salesKey") ?? undefined,
              }
            : null,
        };

        telemetryBeginUser(telemetryUser);
      }
    } else if (!isAuthenticated) {
      telemetryEndUser();
    }
    return () => {
      telemetryEndUser();
    };
  }, [
    isAuthenticated,
    isLoading,
    error,
    getClaimValue,
    getClaimValueAsBoolean,
    isCustomerAdmin,
    storefrontId,
    storefrontsLoaded,
    storefrontsFailed,
  ]);

  if (isLoading) {
    return <Loading />;
  }

  if (error && !shouldBypassAuth0) {
    return (
      <EmotionThemeProvider theme={currentTheme}>
        <ThemeProvider theme={currentTheme}>
          <CssBaseline />
          <AuthError />
        </ThemeProvider>
      </EmotionThemeProvider>
    );
  }

  return (
    <EmotionThemeProvider theme={currentTheme}>
      <ThemeProvider theme={currentTheme}>
        <CssBaseline />
        <UserFlow />
        {SynthScenarioPanel && isAuthenticated && (
          <Suspense fallback={null}>
            <SynthScenarioPanel />
          </Suspense>
        )}
        <ErrorBoundary browserType={browserType}>
          <Routes>
            <>
              <Route
                path={"/:prefix?/report-center/customPbi"}
                element={
                  <FeatureComponent authRequired showDashboard>
                    <CustomPbi />
                  </FeatureComponent>
                }
              />
              <Route
                path={"/:prefix?/report-center"}
                element={
                  <FeatureComponent authRequired showDashboard>
                    <ReportCenter />
                  </FeatureComponent>
                }
              />
              <Route
                path={"/:prefix?/report-center/cloud-consumption"}
                element={
                  <FeatureComponent authRequired showDashboard>
                    <CloudConsumptionReport />
                  </FeatureComponent>
                }
              />
              <Route
                path={"/:prefix?/report-center/cloud-governance"}
                element={
                  <FeatureComponent authRequired showDashboard>
                    <CloudGovernanceReport />
                  </FeatureComponent>
                }
              />
              <Route
                path={"/:prefix?/report-center/microsoft-adoption-dashboard/*"}
                element={
                  <FeatureComponent
                    authRequired
                    showDashboard
                    validateDrawerPath
                  >
                    <MicrosoftAdoptionDashboard />
                  </FeatureComponent>
                }
              />
              <Route
                path={"/:prefix?/report-center/cisco-install-base-analysis"}
                element={
                  <FeatureComponent authRequired showDashboard>
                    <CiscoIbaDashboard />
                  </FeatureComponent>
                }
              />
              <Route
                path={"/:prefix?/report-center/aged-inventory"}
                element={
                  <FeatureComponent authRequired showDashboard>
                    <AgedInventoryPage />
                  </FeatureComponent>
                }
              />
              <Route
                path={"/:prefix?/report-center/orders"}
                element={
                  <FeatureComponent authRequired showDashboard>
                    <OrderReportPage />
                  </FeatureComponent>
                }
              />
              <Route
                path="/:prefix?/report-center/warehouse"
                element={
                  <FeatureComponent authRequired showDashboard>
                    <IdcsReportPage />
                  </FeatureComponent>
                }
              />
              <Route
                path={"/:prefix?/report-center/service-report"}
                element={
                  <FeatureComponent authRequired showDashboard>
                    <ReportCenterServiceReport />
                  </FeatureComponent>
                }
              />
              <Route
                path={"/:prefix?/services/reports"}
                element={
                  <FeatureComponent authRequired showDashboard>
                    <ReportCenterServiceReport />
                  </FeatureComponent>
                }
              />
              <Route
                path={"/:prefix?/report-center/microsoft-insights"}
                element={
                  <FeatureComponent authRequired showDashboard>
                    <MicrosoftInsightsReportPage />
                  </FeatureComponent>
                }
              />
              <Route
                path={"/:prefix?/report-center/create/:datasetId"}
                element={
                  <FeatureComponent authRequired showDashboard>
                    <NewReport />
                  </FeatureComponent>
                }
              />
              <Route
                path={"/:prefix?/report-center/report/:reportId"}
                element={
                  <FeatureComponent authRequired showDashboard>
                    <Report />
                  </FeatureComponent>
                }
              />
              <Route
                path={"/:prefix?/report-center/edit/:reportId"}
                element={
                  <FeatureComponent authRequired showDashboard>
                    <EditReport />
                  </FeatureComponent>
                }
              />
            </>
            <Route
              path={"/:prefix?/support-center/message-center"}
              element={
                <FeatureComponent authRequired showDashboard>
                  <MessageCenter />
                </FeatureComponent>
              }
            />
            <Route
              path={"/:prefix?/support-center/message-center/:id"}
              element={
                <FeatureComponent authRequired showDashboard>
                  <MessageCenter />
                </FeatureComponent>
              }
            />
            <Route
              path="/valuestreams/asg"
              element={
                <SetWindowLocation
                  location={
                    "https://shiandms.sharepoint.com/sites/SHIOne/SitePages/ASG-Team.aspx"
                  }
                />
              }
            />
            <Route
              path="/valuestreams/msp"
              element={
                <SetWindowLocation
                  location={
                    "https://shiandms.sharepoint.com/sites/SHIOne/SitePages/MSP-Team.aspx"
                  }
                />
              }
            />
            <Route
              path="/valuestreams/revenue"
              element={
                <SetWindowLocation
                  location={
                    "https://shiandms.sharepoint.com/sites/SHIOne/SitePages/Revenue-Team.aspx"
                  }
                />
              }
            />
            <Route
              path="/valuestreams/cisco"
              element={
                <SetWindowLocation
                  location={
                    "https://shiandms.sharepoint.com/sites/SHIOne/SitePages/Cisco-Team.aspx"
                  }
                />
              }
            />
            <Route
              path="/passwordlessLogin/:emailAddress/"
              element={<PasswordlessLogin targetPath="/dashboards/home" />}
            />
            <Route path="/:prefix?/logout" element={<LogoutComponent />} />
            <Route
              path="/:prefix?/login"
              element={
                <FeatureComponent>
                  <LoginComponent />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/logout/saml"
              element={<SamlLogoutComponent />}
            />
            <Route
              path="/:prefix?/unity/optIn"
              element={
                <FeatureComponent authRequired>
                  <OptIntoUnity />
                </FeatureComponent>
              }
            />
            <Route
              path="/welcomeCallback"
              element={
                <FeatureComponent>
                  <ReactRouterClassWrapper>
                    {(props) => <WelcomeCallback {...props} />}
                  </ReactRouterClassWrapper>
                </FeatureComponent>
              }
            />
            <Route
              path="/success-request-callback"
              element={
                <FeatureComponent>
                  <SuccessRequestCallback />
                </FeatureComponent>
              }
            />
            <Route
              path="/impersonation-bridge"
              element={<ImpersonationBridge />}
            />
            <Route
              path="/connectCallback"
              element={
                <FeatureComponent>
                  <Microsoft365ReportingRegistrationCallback />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/dashboards/home/*"
              element={
                <FeatureComponent authRequired showDashboard validateDrawerPath>
                  <HomeFeatureFlagged />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/dashboards/home/*"
              element={
                <FeatureComponent authRequired showDashboard validateDrawerPath>
                  <HomeFeatureFlagged />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/cloud/monitoring"
              element={
                <FeatureComponent authRequired showDashboard>
                  <Monitoring />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/cloud/consumption"
              element={
                <FeatureComponent authRequired showDashboard>
                  <CloudConsumptionPage />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/support-center/new-request/*"
              element={
                <FeatureComponent authRequired showDashboard>
                  <RequestLandingPage />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/support-center/new-request/:ticketTypeName/:formName"
              element={
                <FeatureComponent authRequired showDashboard>
                  <SubmitRequestHandler />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/support-center/mydevices"
              element={
                <FeatureComponent authRequired showDashboard>
                  <DeviceList />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/support-center/submit-request/:type?"
              element={
                <FeatureComponent authRequired showDashboard>
                  <SubmitRequest />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/cloud/*"
              element={
                <FeatureComponent authRequired showDashboard validateDrawerPath>
                  <CloudDashboard />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/assets-dashboard/*"
              element={
                <FeatureComponent authRequired showDashboard validateDrawerPath>
                  <AssetDashboard />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/assets/lifecycle-management"
              element={
                <FeatureComponent authRequired showDashboard>
                  <AssetLifecycleManagementPage />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/assets/asset-inventory"
              element={
                <FeatureComponent authRequired showDashboard>
                  <AssetInventory />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/assets/discovered-assets"
              element={
                <FeatureComponent authRequired showDashboard>
                  <DiscoveredAssets />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/assets/discovered-assets/:tabId"
              element={
                <FeatureComponent authRequired showDashboard>
                  <DiscoveredAssets />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/assets/purchased-assets/*"
              element={
                <FeatureComponent authRequired showDashboard validateDrawerPath>
                  <PurchasedAssets />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/manufacturer-insights"
              element={
                <FeatureComponent authRequired showDashboard>
                  <ManufacturerInsightsPage />
                </FeatureComponent>
              }
            />

            <Route
              path="/:prefix?/assets/managed-devices/*"
              element={
                <Navigate
                  to={AppendIfPrefixExists("/assets/managed-assets")}
                  replace
                />
              }
            />

            <Route
              path="/:prefix?/assets/managed-assets/:tabId?"
              element={
                <FeatureComponent authRequired showDashboard>
                  <ManagedAssets />
                </FeatureComponent>
              }
            />

            <Route
              path="/:prefix?/assets/integration-center"
              element={
                <FeatureComponent authRequired showDashboard>
                  <IntegrationCenter />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/assets/integration-center/:tabId/*"
              element={
                <FeatureComponent authRequired showDashboard validateDrawerPath>
                  <IntegrationCenter />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/assets/locations"
              element={
                <FeatureComponent authRequired showDashboard>
                  <AssetLocations />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/assets/product-catalog/products"
              element={
                <FeatureComponent authRequired showDashboard>
                  <AssetProductCatalog />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/assets/product-catalog/lifecycle"
              element={
                <FeatureComponent authRequired showDashboard>
                  <AssetProductLifecycle />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/assets/vendor-contracts"
              element={
                <FeatureComponent authRequired showDashboard>
                  <AssetContracts />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/assets/warehouse/*"
              element={
                <Navigate
                  to={AppendIfPrefixExists("/assets/integration-center")}
                  replace
                />
              }
            />
            <Route
              path="/:prefix?/assets/integration-center/orders/:id"
              element={
                <FeatureComponent authRequired showDashboard>
                  <WarehouseOrderDetail />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/assets/integration-center/orders/transfer/:id"
              element={
                <FeatureComponent authRequired showDashboard>
                  <WarehouseOrder orderItem={undefined} />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/assets/integration-center/orders/ipldemotracking/:id/:trackNum"
              element={<DemoShipping />}
            />

            <Route
              path="/:prefix?/assets/integration-center/orders/integration-center/:id"
              element={
                <FeatureComponent authRequired showDashboard>
                  <IntegrationCenterOrder orderItem={undefined} />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/assets/integration-center/reports"
              element={
                <FeatureComponent authRequired showDashboard>
                  <IdcsReportPage />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/assets/cisco-install-base-analysis"
              element={
                <FeatureComponent authRequired showDashboard>
                  <CiscoIbaDashboard />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/assets/aged-inventory"
              element={
                <FeatureComponent authRequired showDashboard>
                  <AgedInventoryPage />
                </FeatureComponent>
              }
            />
            <Route
              path="/internal/service-requests"
              element={
                <FeatureComponent authRequired>
                  <InternalServiceRequests />
                </FeatureComponent>
              }
            />
            <Route
              path="/internal/service-requests/:id"
              element={
                <FeatureComponent authRequired>
                  <InternalServiceRequestDetails />
                </FeatureComponent>
              }
            />
            <Route
              path="/internal/new-request/:ticketTypeName/:formName"
              element={
                <FeatureComponent authRequired>
                  <InternalNewRequestForm />
                </FeatureComponent>
              }
            />
            <Route
              path="/internal/new-request/*"
              element={
                <FeatureComponent authRequired>
                  <InternalNewRequest />
                </FeatureComponent>
              }
            />
            <Route
              path="/internal/service-request-mapping"
              element={
                <FeatureComponent authRequired>
                  <InternalServiceRequestMapping />
                </FeatureComponent>
              }
            />
            <Route
              path="/internal/docs"
              element={
                <FeatureComponent authRequired>
                  <ArchbeeShiInternal />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/orders/management/*"
              element={
                <FeatureComponent
                  authRequired
                  showDashboard
                  validateDrawerPath
                  allowedOverlayPath="/:prefix?/orders/management/email-order-invoices/:orderKey"
                >
                  <ViewOrdersPage />
                </FeatureComponent>
              }
            />
            {invoicePortalEnabled && (
              <Route
                path="/:prefix?/invoices/management/*"
                element={
                  <FeatureComponent
                    authRequired
                    showDashboard
                    validateDrawerPath
                    allowedOverlayPath="/:prefix?/invoices/management/email-invoices"
                  >
                    <ViewInvoicesPage />
                  </FeatureComponent>
                }
              />
            )}
            <Route
              path="/:prefix?/cart/*"
              element={
                <FeatureComponent
                  authRequired
                  showDashboard
                  validateDrawerPath
                  allowedOverlayPath={[
                    "/:prefix?/cart/save-quote",
                    "/:prefix?/cart/leasing-inquiry",
                  ]}
                >
                  <StorefrontRouteGuard>
                    <CartPageRoute />
                  </StorefrontRouteGuard>
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/checkout"
              element={
                <FeatureComponent authRequired showDashboard>
                  <StorefrontRouteGuard>
                    <CheckoutCorePage />
                  </StorefrontRouteGuard>
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/checkout/success"
              element={
                <FeatureComponent authRequired showDashboard>
                  <StorefrontRouteGuard>
                    <CheckoutSuccessPage />
                  </StorefrontRouteGuard>
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/orders/details/:salesOrder/*"
              element={
                <FeatureComponent
                  authRequired
                  showDashboard
                  validateDrawerPath
                  allowedOverlayPath="/:prefix?/orders/details/:salesOrder/email-order-invoices/:orderKey"
                >
                  <OrderDetailsPage />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/quotes"
              element={
                // TODO SPU-476: Route uses Auth_Auth (same as /orders/management). Permission
                // enforcement is at the API layer (403). Consider adding a permission-based
                // route guard if the team wants client-side redirect for unauthorized users.
                <FeatureComponent authRequired showDashboard>
                  <QuotePageRoute
                    listPage={ViewQuotesPage}
                    detailPage={QuoteDetailPage}
                  />
                </FeatureComponent>
              }
            />
            {/* SPU-787: list dialogs keep the list as the base; static segments outrank :quoteId. */}
            <Route
              path="/:prefix?/quotes/share/:quoteId"
              element={
                <FeatureComponent authRequired showDashboard>
                  <QuotePageRoute
                    listPage={ViewQuotesPage}
                    detailPage={QuoteDetailPage}
                    forceList
                  />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/quotes/refresh/:quoteId"
              element={
                <FeatureComponent authRequired showDashboard>
                  <QuotePageRoute
                    listPage={ViewQuotesPage}
                    detailPage={QuoteDetailPage}
                    forceList
                  />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/quotes/storefront-mismatch/:quoteId"
              element={
                <FeatureComponent authRequired showDashboard>
                  <QuotePageRoute
                    listPage={ViewQuotesPage}
                    detailPage={QuoteDetailPage}
                    forceList
                  />
                </FeatureComponent>
              }
            />
            {/* The static "create" segment outranks :quoteId; permission enforcement stays at the API layer. */}
            {createQuotesFlag && (
              <Route
                path="/:prefix?/quotes/create/*"
                element={
                  <FeatureComponent
                    authRequired
                    showDashboard
                    validateDrawerPath
                  >
                    <StorefrontRouteGuard>
                      <CreateQuotePage />
                    </StorefrontRouteGuard>
                  </FeatureComponent>
                }
              />
            )}
            {/* SPU-472: permission enforcement remains at the API layer (403) as with the list route. */}
            <Route
              path="/:prefix?/quotes/:quoteId/*"
              element={
                <FeatureComponent
                  authRequired
                  showDashboard
                  validateDrawerPath
                  drawerBasePath="/:prefix?/quotes/:quoteId"
                  allowedOverlayPath={QUOTE_DETAIL_OVERLAY_PATHS}
                >
                  <QuotePageRoute
                    listPage={ViewQuotesPage}
                    detailPage={QuoteDetailPage}
                  />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/support-center/requests"
              element={
                <FeatureComponent authRequired showDashboard>
                  <ServiceRequestsPage />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/support-center/requests/:id"
              element={
                <FeatureComponent authRequired showDashboard>
                  <ServiceRequestDetails />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/standards-shop/*"
              element={
                <FeatureComponent
                  authRequired
                  showDashboard
                  validateDrawerPath
                  allowedOverlayPath={STANDARDS_SHOP_OVERLAY_PATHS}
                >
                  <StorefrontRouteGuard>
                    <StandardsShopRoutePage />
                  </StorefrontRouteGuard>
                </FeatureComponent>
              }
            />
            <Route
              path={PRODUCT_LISTING_ROUTE_PATH}
              element={
                <FeatureComponent authRequired showDashboard>
                  <StorefrontRouteGuard>
                    <ProductListingRoutePage />
                  </StorefrontRouteGuard>
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/standards-management/*"
              element={
                canManageStandards ? (
                  <FeatureComponent
                    authRequired
                    showDashboard
                    validateDrawerPath
                    allowedOverlayPath={STANDARDS_MANAGEMENT_OVERLAY_PATHS}
                  >
                    <StandardsManagementPage />
                  </FeatureComponent>
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/:prefix?/services/contracts"
              element={
                <FeatureComponent authRequired showDashboard>
                  <Contracts />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/services/contracts-v2"
              element={
                <FeatureComponent authRequired showDashboard>
                  <ContractsPage />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/services/billing/*"
              element={
                <FeatureComponent
                  authRequired
                  showDashboard
                  validateDrawerPath
                  allowedOverlayPath="/:prefix?/services/billing/email-invoices"
                >
                  <InvoiceLandingPage />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/services/service-engagements/:projectId"
              element={
                <FeatureComponent authRequired showDashboard>
                  <ProjectDetailsV2 />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/services/service-engagements"
              element={
                <FeatureComponent authRequired showDashboard>
                  <Projects />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/services/projects/*"
              element={
                <Navigate
                  to={AppendIfPrefixExists("/services/service-engagements")}
                  replace
                />
              }
            />
            <Route
              path="/:prefix?/services/invoices/*"
              element={
                <FeatureComponent
                  authRequired
                  showDashboard
                  validateDrawerPath
                  allowedOverlayPath="/:prefix?/services/invoices/email-invoices"
                >
                  <ServicesBillingPage />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/shi-internal/projects"
              element={
                <FeatureComponent authRequired showDashboard>
                  <Projects />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/services/contracts/:contractId/*"
              element={
                <FeatureComponent
                  authRequired
                  showDashboard
                  validateDrawerPath
                  drawerBasePath="/:prefix?/services/contracts/:contractId"
                  allowedOverlayPath="/:prefix?/services/contracts/:contractId/email-invoices"
                >
                  <ContractDetailPage />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/support-center/it-knowledgebase"
              element={
                <FeatureComponent authRequired showDashboard>
                  <ITKnowledgeBase />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/cloud/subscriptions/*"
              element={
                <FeatureComponent
                  authRequired
                  showDashboard
                  validateDrawerPath
                  allowedOverlayPath={CLOUD_SUBSCRIPTIONS_OVERLAY_PATHS}
                >
                  <CloudSubscriptions />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/cloud/subscription-management/*"
              element={
                <FeatureComponent
                  authRequired
                  showDashboard
                  validateDrawerPath
                  allowedOverlayPath={CSP_OVERLAY_PATHS}
                >
                  <SubscriptionManagement />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/cloud/cost-centers/*"
              element={
                <FeatureComponent
                  authRequired
                  showDashboard
                  validateDrawerPath
                  allowedOverlayPath={COST_CENTERS_OVERLAY_PATHS}
                >
                  <CostCentersPage />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/cloud/azure/backup"
              element={
                <FeatureComponent authRequired showDashboard>
                  <AzureBackup />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/cloud/azure/blueprints"
              element={
                <FeatureComponent authRequired showDashboard>
                  <Blueprints platform="azure" />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/cloud/azure/securitycompliance/*"
              element={
                <FeatureComponent
                  authRequired
                  showDashboard
                  validateDrawerPath
                  allowedOverlayPath={AZURE_SECURITY_COMPLIANCE_OVERLAY_PATHS}
                >
                  <AzureSecurityCompliance />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/cloud/azure/governance"
              element={
                <FeatureComponent authRequired showDashboard>
                  <AzureGovernanceReports />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/cloud/azure/recommendations/*"
              element={
                <FeatureComponent
                  authRequired
                  showDashboard
                  validateDrawerPath
                  allowedOverlayPath={AZURE_RECOMMENDATIONS_OVERLAY_PATHS}
                >
                  <AzureSimpleRecommendations />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/cloud/azure/reports/all-up/recommendations"
              element={
                <FeatureComponent authRequired showDashboard>
                  {" "}
                  <AzureAllUpReport />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/cloud/azure/reports"
              element={
                <FeatureComponent authRequired showDashboard>
                  <AzureReport />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/shi-internal/cloudreports"
              element={
                <FeatureComponent authRequired showDashboard>
                  <CloudReports />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/shi-internal/cloudOnboardingReport"
              element={
                <FeatureComponent authRequired showDashboard>
                  <CloudOnboardingReport />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/cloud/aws/reports"
              element={
                <FeatureComponent authRequired showDashboard>
                  <AwsReport />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/cloud/aws/blueprints"
              element={
                <FeatureComponent authRequired showDashboard>
                  <Blueprints platform="aws" />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/cloud/aws/accounts/*"
              element={
                <FeatureComponent
                  authRequired
                  showDashboard
                  validateDrawerPath
                  allowedOverlayPath={AWS_ACCOUNTS_OVERLAY_PATHS}
                >
                  <AwsAccounts />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/cloud/aws/cat/*"
              element={
                <FeatureComponent
                  authRequired
                  showDashboard
                  validateDrawerPath
                  allowedOverlayPath={AWS_COST_ALLOCATION_TAGS_OVERLAY_PATHS}
                >
                  <AwsCostAllocationTags />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/cloud/aws/invoices/*"
              element={
                <FeatureComponent
                  authRequired
                  showDashboard
                  validateDrawerPath
                  allowedOverlayPath="/:prefix?/cloud/aws/invoices/email-invoices"
                >
                  <AwsInvoiceList />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/cloud/aws/monitoring/:id?"
              element={
                <FeatureComponent authRequired showDashboard>
                  <AwsMonitoring />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/cloud/aws/governance"
              element={
                <FeatureComponent authRequired showDashboard>
                  <AwsGovernanceReports />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/cloud/aws/recommendations/*"
              element={
                <FeatureComponent
                  authRequired
                  showDashboard
                  validateDrawerPath
                  allowedOverlayPath={AWS_RECOMMENDATIONS_OVERLAY_PATHS}
                >
                  <AwsSimpleRecommendations />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/cloud/azure/subscriptions/*"
              element={
                <FeatureComponent
                  authRequired
                  showDashboard
                  validateDrawerPath
                  allowedOverlayPath={AZURE_SUBSCRIPTIONS_OVERLAY_PATHS}
                >
                  <AzureAccounts />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/cloud/azure/monitoring/:id?"
              element={
                <FeatureComponent authRequired showDashboard>
                  <AzureMonitoring />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/cloud/azure/invoices/*"
              element={
                <FeatureComponent
                  authRequired
                  showDashboard
                  validateDrawerPath
                  allowedOverlayPath="/:prefix?/cloud/azure/invoices/email-invoices"
                >
                  <AzureInvoiceList />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/cloud/gcp/invoices/*"
              element={
                <FeatureComponent
                  authRequired
                  showDashboard
                  validateDrawerPath
                  allowedOverlayPath="/:prefix?/cloud/gcp/invoices/email-invoices"
                >
                  <GcpInvoiceList />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/cloud/gcp/projects"
              element={
                <FeatureComponent authRequired showDashboard>
                  <GcpProjects />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/cloud/gcp/reports"
              element={
                <FeatureComponent authRequired showDashboard>
                  <GcpReport />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/cloud/office365/subscriptions"
              element={
                <FeatureComponent authRequired showDashboard>
                  <Office365Accounts />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/cloud/office365/invoices/*"
              element={
                <FeatureComponent
                  authRequired
                  showDashboard
                  validateDrawerPath
                  allowedOverlayPath="/:prefix?/cloud/office365/invoices/email-invoices"
                >
                  <Office365InvoiceList />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/cloud/datadog/reports"
              element={
                <FeatureComponent authRequired showDashboard>
                  <DatadogReportPage />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/assessments"
              element={
                <FeatureComponent authRequired showDashboard>
                  <AssessmentsPage />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/assessments/security-posture-review"
              element={
                <FeatureComponent authRequired showDashboard>
                  <AssessmentDashboardPage
                    program={"security-posture-review"}
                  />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/assessments/security-posture-review/:type"
              element={
                <FeatureComponent authRequired showDashboard>
                  <AssessmentViewPage program={"security-posture-review"} />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/assessments/security-posture-review-v2"
              element={
                <FeatureComponent authRequired showDashboard>
                  <AssessmentDashboardPage
                    program={"security-posture-review-v2"}
                  />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/assessments/security-posture-review-v2/:type"
              element={
                <FeatureComponent authRequired showDashboard>
                  <AssessmentViewPage program={"security-posture-review-v2"} />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/assessments/ai-readiness"
              element={
                <FeatureComponent authRequired showDashboard>
                  <AssessmentDashboardPage program={"ai-readiness"} />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/assessments/ai-readiness/:type"
              element={
                <FeatureComponent authRequired showDashboard>
                  <AssessmentViewPage program={"ai-readiness"} />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/assessments/dev-ops-posture-review"
              element={
                <DevOpsPostureReviewRouteGuard>
                  <FeatureComponent authRequired showDashboard>
                    <AssessmentDashboardPage
                      program={"dev-ops-posture-review"}
                    />
                  </FeatureComponent>
                </DevOpsPostureReviewRouteGuard>
              }
            />
            <Route
              path="/:prefix?/assessments/dev-ops-posture-review/:type"
              element={
                <DevOpsPostureReviewRouteGuard>
                  <FeatureComponent authRequired showDashboard>
                    <AssessmentViewPage program={"dev-ops-posture-review"} />
                  </FeatureComponent>
                </DevOpsPostureReviewRouteGuard>
              }
            />
            <Route
              path="/:prefix?/assessments/aws-maturity-review"
              element={
                <FeatureComponent authRequired showDashboard>
                  <AssessmentDashboardPage program={"aws-maturity-review"} />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/assessments/aws-maturity-review/:type"
              element={
                <FeatureComponent authRequired showDashboard>
                  <AssessmentViewPage program={"aws-maturity-review"} />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/assessments/tpx"
              element={
                <FeatureComponent authRequired showDashboard>
                  <AssessmentDashboardPage program={"tpx"} />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/assessments/tpx/:type"
              element={
                <FeatureComponent authRequired showDashboard>
                  <AssessmentViewPage program={"tpx"} />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/assessments/cloud-matrix/*"
              element={
                <FeatureComponent authRequired showDashboard>
                  <CloudMatrixPage />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/assessments/microsoft-insights"
              element={
                <FeatureComponent authRequired showDashboard>
                  <MicrosoftInsightsAssessmentPage />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/projects/:projectId/:tabId?/*"
              element={
                <FeatureComponent authRequired showDashboard validateDrawerPath>
                  <ItProjectDetailPage />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/projects"
              element={
                <FeatureComponent authRequired showDashboard>
                  <ItProjectsPage />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/shi-internal/myprojects"
              element={
                <FeatureComponent authRequired showDashboard>
                  <MyProjectList />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/shi-internal/contractmapping"
              element={
                <FeatureComponent authRequired showDashboard>
                  <ContractMappings />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/shi-internal/service-request-mapping"
              element={
                <FeatureComponent authRequired showDashboard>
                  <ServiceRequestMappings />
                </FeatureComponent>
              }
            />
            <Route
              path="/shi-internal/cisco-reports/iba"
              element={
                <FeatureComponent authRequired showDashboard>
                  <CiscoIba />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/shi-internal/docs"
              element={
                <FeatureComponent authRequired showDashboard>
                  <ArchbeeShiInternal />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/shi-internal/marketplaceAccounts/*"
              element={
                <FeatureComponent
                  authRequired
                  showDashboard
                  validateDrawerPath
                  allowedOverlayPath={MARKETPLACE_ACCOUNTS_OVERLAY_PATHS}
                >
                  <MarketplaceAccounts />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/labs"
              element={
                <FeatureComponent authRequired showDashboard>
                  <Labs />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/service-catalog/*"
              element={
                <FeatureComponent
                  authRequired
                  showDashboard
                  validateDrawerPath
                  allowedOverlayPath={SERVICE_CATALOG_OVERLAY_PATHS}
                >
                  <ServiceCatalog />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/shi-internal/address-field-test-page"
              element={
                <FeatureComponent authRequired showDashboard>
                  <AddressFieldTestPage />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/settings/integrations"
              element={
                <FeatureComponent authRequired showDashboard>
                  <Integrations />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/shi-internal/mapping-engine/:id?/mapping"
              element={
                <FeatureComponent showDashboard authRequired>
                  <MappingPage />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/settings/user-management"
              element={
                <FeatureComponent authRequired showDashboard>
                  <UserManagement />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/settings/team-management/:id?"
              element={
                <FeatureComponent authRequired showDashboard>
                  <TeamsManagement />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/settings/cost-centers"
              element={
                <Navigate
                  to={AppendIfPrefixExists("/cloud/cost-centers")}
                  replace
                />
              }
            />
            <Route
              path="/:prefix?/settings/storefronts/:storefrontId/:tabRoute?/*"
              element={
                <FeatureComponent
                  authRequired
                  showDashboard
                  validateDrawerPath
                  drawerBasePath="/:prefix?/settings/storefronts/:storefrontId/:tabRoute?"
                  allowedOverlayPath={STOREFRONT_OVERLAY_PATHS}
                >
                  <StorefrontManagement />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/settings/storefronts"
              element={
                <FeatureComponent authRequired showDashboard>
                  <Storefronts />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/my-addresses"
              element={
                <FeatureComponent authRequired showDashboard>
                  <MyAddressesPage />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/settings/:section?"
              element={
                <FeatureComponent authRequired showDashboard>
                  <ReactRouterClassWrapper>
                    {(props) => <Settings {...props} />}
                  </ReactRouterClassWrapper>
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/attachment/:guid"
              element={
                <FeatureComponent authRequired>
                  <ReactRouterClassWrapper>
                    {() => <DownloadAttachment />}
                  </ReactRouterClassWrapper>
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/userprofile"
              element={
                <FeatureComponent authRequired showDashboard>
                  <UserProfile />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/organizationprofile"
              element={
                <FeatureComponent authRequired showDashboard>
                  <OrganizationProfile />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/demos"
              element={
                <FeatureComponent authRequired showDashboard>
                  <DemoAccounts />
                </FeatureComponent>
              }
            />
            <Route
              path="/welcome/:id"
              element={
                <FeatureComponent>
                  <SetupWizard />
                </FeatureComponent>
              }
            />
            <Route
              path="/register/invite/:linkId"
              element={
                <FeatureComponent>
                  <RegisterViaInviteLinkPage />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/noprofile"
              element={
                <FeatureComponent>
                  <ErrorPage />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/invalidurl"
              element={
                <FeatureComponent>
                  <InvalidUrlPage />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/register"
              element={
                <FeatureComponent authRequired>
                  <RegisterPage />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?"
              element={
                <FeatureComponent>
                  <Navigate
                    to={{
                      pathname: "/login",
                    }}
                  />
                </FeatureComponent>
              }
            />
            <Route
              path="*"
              element={
                <FeatureComponent>
                  <div>Invalid URL</div>
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/shi-internal/sample-dashboard-1/*"
              element={
                <FeatureComponent authRequired showDashboard validateDrawerPath>
                  <CustomerExperienceTwoColumnDashboard
                    dashboardId={"sample-dashboard-1"}
                  />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/shi-internal/sample-dashboard-2/*"
              element={
                <FeatureComponent authRequired showDashboard validateDrawerPath>
                  <CustomerExperienceTwoColumnDashboard
                    dashboardId={"sample-dashboard-2"}
                  />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/home/*"
              element={
                <FeatureComponent authRequired showDashboard validateDrawerPath>
                  {unityDashboard ? (
                    <UnifiedHomeDashboard />
                  ) : (
                    <HomeDashboard />
                  )}
                </FeatureComponent>
              }
            />
            <Route
              path={"/:prefix?/my-account-team"}
              element={
                <FeatureComponent authRequired showDashboard>
                  <ApiConfigProvider
                    baseUrl={import.meta.env.API_ENDPOINT}
                    getToken={getBearerTokenSafe}
                  >
                    <BusinessReviewPage />
                  </ApiConfigProvider>
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/support/*"
              element={
                <FeatureComponent authRequired showDashboard validateDrawerPath>
                  <CustomerExperienceTwoColumnDashboard
                    dashboardId={"customer-experience-support-dashboard"}
                    header={"Services & Support"}
                  />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/customer-experience-support-dashboard"
              element={
                <Navigate
                  to={{
                    pathname: AppendIfPrefixExists("/support"),
                  }}
                />
              }
            />
            <Route
              path="/:prefix?/services/*"
              element={
                <FeatureComponent authRequired showDashboard validateDrawerPath>
                  <CustomerExperienceTwoColumnDashboard
                    dashboardId={"customer-experience-services-dashboard"}
                    header={"Services"}
                  />
                </FeatureComponent>
              }
            />
            <Route
              path="/:prefix?/customer-experience-services-dashboard"
              element={
                <Navigate
                  to={{
                    pathname: AppendIfPrefixExists("/services"),
                  }}
                />
              }
            />
            <Route
              path="/:prefix?/shi-internal/customer-experience-cloud-dashboard/*"
              element={
                <FeatureComponent authRequired showDashboard validateDrawerPath>
                  <CustomerExperienceTwoColumnDashboard
                    dashboardId={"customer-experience-cloud-dashboard"}
                  />
                </FeatureComponent>
              }
            />
            <Route
              path={"/:prefix?/roadmaps/:roadmapId"}
              element={
                <FeatureComponent authRequired showDashboard>
                  <RoadmapsRecommendationsMiddleware>
                    <ApiConfigProvider
                      baseUrl={import.meta.env.API_ENDPOINT}
                      getToken={getBearerTokenSafe}
                    >
                      <RoadmapDetailPage
                        useLinkedItProjects={useGetLinkedItProjectsByRoadmap}
                        buildItProjectHref={(project) =>
                          AppendIfPrefixExists(
                            `/projects/${project.itProjectId}/orders`,
                          )
                        }
                      />
                    </ApiConfigProvider>
                  </RoadmapsRecommendationsMiddleware>
                </FeatureComponent>
              }
            />
            <Route
              path={"/:prefix?/roadmaps"}
              element={
                <FeatureComponent authRequired showDashboard>
                  <RoadmapsRecommendationsMiddleware>
                    <ApiConfigProvider
                      baseUrl={import.meta.env.API_ENDPOINT}
                      getToken={getBearerTokenSafe}
                    >
                      <Roadmaps />
                    </ApiConfigProvider>
                  </RoadmapsRecommendationsMiddleware>
                </FeatureComponent>
              }
            />
            <Route
              path={"/:prefix?/services/catalyst/*"}
              element={
                <FeatureComponent authRequired showDashboard validateDrawerPath>
                  <RoadmapsRecommendationsMiddleware>
                    <CatalystDashboard />
                  </RoadmapsRecommendationsMiddleware>
                </FeatureComponent>
              }
            />
            <Route
              path={"/:prefix?/recommendations"}
              element={
                <FeatureComponent authRequired showDashboard>
                  <RoadmapsRecommendationsMiddleware>
                    <Recommendations />
                  </RoadmapsRecommendationsMiddleware>
                </FeatureComponent>
              }
            />
          </Routes>
        </ErrorBoundary>
      </ThemeProvider>
    </EmotionThemeProvider>
  );
}

export default App;
